import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import DatePicker, { registerLocale } from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import '../../styles/datepicker.css'
import fr from 'date-fns/locale/fr'

import { addDays } from 'date-fns'

registerLocale('fr', fr)
import api from '../../services/api'
import axios from 'axios'
import { useAuthStore } from '../../stores/authStore'
import {
    ArrowLeft,
    ArrowRight,
    Check,
    Search,
    Plus,
    Trash2,
    Loader2,
    User,
    Users,
    FileText,
    Calendar,
    MapPin
} from 'lucide-react'
import toast from 'react-hot-toast'
import './RendezvousAddPage.css'
import AddressAutocomplete from '../../components/AddressAutocomplete'

const steps = [
    { key: 'client', icon: User },
    { key: 'famille', icon: Users },
    { key: 'resume', icon: FileText }
]

export default function RendezvousAddPage() {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { user } = useAuthStore()

    const [currentStep, setCurrentStep] = useState(0)
    const [loading, setLoading] = useState(false)
    const [searchPhone, setSearchPhone] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [searching, setSearching] = useState(false)
    const [searchPerformed, setSearchPerformed] = useState(false)
    const [selectedClient, setSelectedClient] = useState(null)
    const [addressLoading, setAddressLoading] = useState(false)
    const [referenceData, setReferenceData] = useState({
        lamal: [],
        lca: [],
        etats_sante: [],
        config: {}
    })
    const [ageType, setAgeType] = useState('dob')

    const applyPhoneMask = (value, mask) => {
        if (!value) return value
        let digits = value.replace(/\D/g, '')
        if (digits.length > 10) digits = digits.slice(0, 10)

        let res = ''
        if (digits.length > 0) res += digits.substring(0, 3)
        if (digits.length > 3) res += ' ' + digits.substring(3, 6)
        if (digits.length > 6) res += ' ' + digits.substring(6, 8)
        if (digits.length > 8) res += ' ' + digits.substring(8, 10)

        return res
    }

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        trigger,
        control,
        formState: { errors, isValid },
        getValues
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            rdvDate: '',
            rdvTime: '',
            commentaireAgent_rdv: '',
            client: {
                nom_client: '',
                prenom_client: '',
                natel_client: '',
                fix_client: '',
                natel2_client: '',
                fix2_client: '',
                date_naissance: '',
                age: '',
                adresse_client: {
                    adresse: '',
                    etage: '',
                    npa: '',
                    ville: '',
                    indications: ''
                }
            },
            famille: []
        }
    })

    const { fields: familleFields, append, remove } = useFieldArray({
        control,
        name: 'famille'
    })

    useEffect(() => {
        fetchReferenceData()
    }, [])

    const fetchReferenceData = async () => {
        try {
            const [lamalRes, lcaRes, santéRes, configRes] = await Promise.all([
                api.get('/reference/lamal'),
                api.get('/reference/lca'),
                api.get('/reference/etats-sante'),
                api.get('/reference/config')
            ])

            setReferenceData({
                lamal: lamalRes.data.data.options,
                lca: lcaRes.data.data.options,
                etats_sante: santéRes.data.data.options,
                config: configRes.data.data.config
            })
        } catch (error) {
            console.error('Error fetching reference data:', error)
        }
    }

    const lookupAddress = async (address) => {
        if (!address || address.length < 5) return

        setAddressLoading(true)
        try {
            const response = await axios.get('https://nominatim.openstreetmap.org/search', {
                params: {
                    q: address,
                    format: 'json',
                    addressdetails: 1,
                    countrycodes: 'ch',
                    limit: 1
                }
            })

            if (response.data && response.data.length > 0) {
                const data = response.data[0]
                const addressDetails = data.address

                const postcode = addressDetails.postcode
                const city = addressDetails.city || addressDetails.town || addressDetails.village || addressDetails.municipality

                if (postcode) setValue('client.adresse_client.npa', postcode, { shouldValidate: true })
                if (city) setValue('client.adresse_client.ville', city, { shouldValidate: true })
            }
        } catch (error) {
            console.error('Address lookup failed', error)
        } finally {
            setAddressLoading(false)
        }
    }

    const handleSearchClient = async () => {
        if (searchPhone.length < 3) return

        setSearching(true)
        setSearchPerformed(true)
        try {
            const response = await api.get('/clients/search', { params: { phone: searchPhone } })
            setSearchResults(response.data.data.clients)

            // Show toast if no results
            if (response.data.data.clients.length === 0) {
                toast(t('rendezvous.noClientFound'), { icon: '🔍' })
            }
        } catch (error) {
            toast.error(t('common.error'))
            setSearchResults([])
        } finally {
            setSearching(false)
        }
    }

    const handleSelectClient = (client) => {
        setSelectedClient(client)
        setValue('client.nom_client', client.nom_client)
        setValue('client.prenom_client', client.prenom_client)
        setValue('client.natel_client', client.natel_client)
        setValue('client.fix_client', client.fix_client || '')
        setValue('client.natel2_client', client.natel2_client || '')
        setValue('client.fix2_client', client.fix2_client || '')
        setValue('client.adresse_client.adresse', client.adresse_client?.adresse || '')
        setValue('client.adresse_client.etage', client.adresse_client?.etage || '')
        setValue('client.adresse_client.npa', client.adresse_client?.npa || '')
        setValue('client.adresse_client.ville', client.adresse_client?.ville || '')
        setValue('client.adresse_client.indications', client.adresse_client?.indications || '')

        // Pre-fill family
        if (client.famille && client.famille.length > 0) {
            client.famille.forEach((member, index) => {
                if (index === 0) {
                    setValue('famille', [member])
                } else {
                    append(member)
                }
            })
        }

        setSearchResults([])
        setSearchPhone('')
        setSearchPerformed(false)
        toast.success(t('rendezvous.clientSelected'))
    }

    const addFamilyMember = () => {
        append({
            typeFamille: 'Adulte',
            prenomFamille: '',
            ageType: 'dob',
            age: '',
            datenaissanceFamille: '',
            etatSanteFamille: 'Excellente',
            franchiseFamille: '',
            lamalFamille: '',
            lcaFamille: '',
            depuisFamille: ''
        })
    }

    const validateStep1 = async () => {
        const fieldsToValidate = [
            'rdvDate',
            'rdvTime',
            'client.nom_client',
            'client.prenom_client',
            'client.natel_client',
            'client.adresse_client.adresse',
            'client.adresse_client.npa',
            'client.adresse_client.ville'
        ]

        if (ageType === 'dob') {
            fieldsToValidate.push('client.date_naissance')
        } else {
            fieldsToValidate.push('client.age')
        }

        const isValid = await trigger(fieldsToValidate)

        if (!isValid) {
            toast.error(t('validation.fixErrors'))
        }

        return isValid
    }

    const nextStep = async (e) => {
        if (e) e.preventDefault()
        if (currentStep === 0) {
            const isValid = await validateStep1()
            if (!isValid) return
        }

        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1)
        }
    }

    const previousStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    const onSubmit = async (data) => {
        setLoading(true)
        try {
            // rdvDate now contains both date and time as ISO string
            const dateHeure_rdv = data.rdvDate

            const payload = {
                dateHeure_rdv,
                commentaireAgent_rdv: data.commentaireAgent_rdv,
                client_id: selectedClient?._id,
                client: {
                    ...data.client,
                    famille: data.famille?.map(m => ({
                        ...m,
                        age: (m.ageType === 'dob' || !m.ageType) ? null : m.age,
                        datenaissanceFamille: m.ageType === 'age' ? null : m.datenaissanceFamille
                    }))
                },
                equipe_id: user.equipes?.[0]?.id
            }

            await api.post('/rendezvous', payload)
            toast.success(t('rendezvous.created'))
            navigate('/rendezvous')
        } catch (error) {
            const data = error.response?.data
            const msg = data?.message

            if (msg === 'APPOINTMENT_DATE_MUST_BE_FUTURE') {
                toast.error(t('validation.APPOINTMENT_DATE_MUST_BE_FUTURE'))
            } else if (msg === 'APPOINTMENT_INTERVAL_INVALID') {
                toast.error(t('validation.APPOINTMENT_INTERVAL_INVALID'))
            } else if (msg === 'APPOINTMENT_TIME_OUT_OF_BOUNDS') {
                toast.error(t('validation.APPOINTMENT_TIME_OUT_OF_BOUNDS'))
            } else if (msg === 'VALIDATION_FAILED' && data?.errors?.length > 0) {
                const firstError = data.errors[0]
                toast.error(t(`validation.${firstError.message}`, { defaultValue: t('validation.fixErrors') }))
            } else {
                toast.error(msg || t('common.error'))
            }
        } finally {
            setLoading(false)
        }
    }

    const formData = watch()
    const familyValues = watch('famille')

    // Config for TimePicker
    const config = referenceData.config || {}
    const getHoursMinutes = (str) => {
        if (!str) return { h: 8, m: 0 }
        const parts = str.split(':')
        return { h: parseInt(parts[0]), m: parseInt(parts[1]) || 0 }
    }

    const startConfig = getHoursMinutes(config.heureDebut || '08:00')
    const endConfig = getHoursMinutes(config.heureFin || '18:00')
    const interval = parseInt(config.intervale || '15')
    const allowPast = referenceData.config.allowPastAppointments === true

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className="wizard-step">
                        <h3>{t('rendezvous.wizard.step1')}</h3>

                        {/* Client Search */}
                        <div className="client-search">
                            <label className="form-label">{t('rendezvous.wizard.searchClient')}</label>
                            <div className="search-row">
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="07..."
                                    value={searchPhone}
                                    onChange={(e) => setSearchPhone(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchClient()}
                                />
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleSearchClient}
                                    disabled={searching || searchPhone.length < 3}
                                >
                                    {searching ? <Loader2 size={18} className="spin" /> : <Search size={18} />}
                                </button>
                            </div>

                            {searchResults.length > 0 && (
                                <div className="search-results">
                                    {searchResults.map((client) => (
                                        <div
                                            key={client._id}
                                            className="search-result-item"
                                            onClick={() => handleSelectClient(client)}
                                        >
                                            <div>
                                                <strong>{client.prenom_client} {client.nom_client}</strong>
                                                <span className="text-muted"> - {client.natel_client}</span>
                                            </div>
                                            <small className="text-muted">
                                                {client.adresse_client?.ville} ({client.adresse_client?.npa})
                                            </small>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {searchPerformed && searchResults.length === 0 && !searching && (
                                <div className="search-no-results warning">
                                    <div className="no-result-icon">
                                        <Search size={24} />
                                    </div>
                                    <div className="no-result-content">
                                        <h4>{t('rendezvous.noClientFound')}</h4>
                                        <p>{t('rendezvous.checkPhoneOrAdd')}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {selectedClient && (
                            <div className="selected-client-badge">
                                <Check size={16} />
                                {t('rendezvous.wizard.existingClient')}: {selectedClient.prenom_client} {selectedClient.nom_client}
                            </div>
                        )}

                        {/* Date & Time + Age/DOB Combined Row */}
                        <div className="form-row">
                            {/* Single Date & Time Field */}
                            <div className="form-group" style={{ flex: 1 }}>
                                <label className="form-label">
                                    <Calendar size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                    {t('rendezvous.dateLabel')} & {t('rendezvous.timeLabel')} *
                                </label>
                                <Controller
                                    control={control}
                                    name="rdvDate"
                                    rules={{ required: t('validation.required') }}
                                    render={({ field }) => {
                                        const dateBase = field.value ? new Date(field.value) : new Date();
                                        return (
                                            <DatePicker
                                                selected={field.value ? new Date(field.value) : null}
                                                onChange={(date) => field.onChange(date ? date.toISOString() : '')}
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={interval}
                                                dateFormat="dd.MM.yyyy HH:mm"
                                                timeCaption="Heure"
                                                timeClassName={(time) => {
                                                    const currentMinutes = time.getHours() * 60 + time.getMinutes()
                                                    const startMinutes = startConfig.h * 60 + startConfig.m
                                                    const endMinutes = endConfig.h * 60 + endConfig.m

                                                    if (currentMinutes < startMinutes || currentMinutes > endMinutes) {
                                                        return "hidden-time-item"
                                                    }
                                                    return ""
                                                }}
                                                className={`form-input ${errors.rdvDate ? 'error' : ''}`}
                                                placeholderText="dd.mm.yyyy HH:mm"
                                                locale={i18n.language.startsWith('fr') ? 'fr' : undefined}
                                                autoComplete='off'
                                                minDate={allowPast ? null : addDays(new Date(), 1)}
                                            />
                                        );
                                    }}
                                />
                                {errors.rdvDate && (
                                    <span className="form-error">{errors.rdvDate.message}</span>
                                )}
                            </div>

                            {/* Age / DOB Section */}
                            <div className="form-group" style={{ flex: 1 }}>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <label className="form-label">Type</label>
                                        <select
                                            className="form-select"
                                            value={ageType}
                                            onChange={(e) => setAgeType(e.target.value)}
                                        >
                                            <option value="dob">Date de naissance</option>
                                            <option value="age">Age</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label className="form-label">
                                            {ageType === 'dob' ? 'Date de naissance' : 'Age'}
                                        </label>
                                        {ageType === 'dob' ? (
                                            <>
                                                <Controller
                                                    control={control}
                                                    name="client.date_naissance"
                                                    rules={{ required: ageType === 'dob' ? t('validation.required') : false }}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            selected={field.value ? new Date(field.value) : null}
                                                            onChange={(date) => field.onChange(date ? date.toISOString() : '')}
                                                            dateFormat="dd.MM.yyyy"
                                                            showYearDropdown
                                                            scrollableYearDropdown
                                                            yearDropdownItemNumber={100}
                                                            className="form-input"
                                                            placeholderText="dd.mm.yyyy"
                                                            locale={i18n.language.startsWith('fr') ? 'fr' : undefined}
                                                            autoComplete='off'
                                                            maxDate={new Date()}
                                                        />
                                                    )}
                                                />
                                                {errors.client?.date_naissance && (
                                                    <span className="form-error">{errors.client.date_naissance.message}</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <input
                                                    type="number"
                                                    className="form-input"
                                                    placeholder="Age"

                                                    {...register('client.age', { required: ageType === 'age' ? t('validation.required') : false })}
                                                />
                                                {errors.client?.age && (
                                                    <span className="form-error">{errors.client.age.message}</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Client Info */}
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">{t('client.lastName')} *</label>
                                <input
                                    type="text"
                                    className={`form-input ${errors.client?.nom_client ? 'error' : ''}`}
                                    {...register('client.nom_client', { required: t('validation.required') })}
                                />
                                {errors.client?.nom_client && (
                                    <span className="form-error">{errors.client.nom_client.message}</span>
                                )}
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('client.firstName')} *</label>
                                <input
                                    type="text"
                                    className={`form-input ${errors.client?.prenom_client ? 'error' : ''}`}
                                    {...register('client.prenom_client', { required: t('validation.required') })}
                                />
                                {errors.client?.prenom_client && (
                                    <span className="form-error">{errors.client.prenom_client.message}</span>
                                )}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">{t('client.mobile')} *</label>
                                <Controller
                                    control={control}
                                    name="client.natel_client"
                                    rules={{ required: t('validation.required') }}
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="tel"
                                            className={`form-input ${errors.client?.natel_client ? 'error' : ''}`}
                                            onChange={(e) => {
                                                const masked = applyPhoneMask(e.target.value, referenceData.config?.phoneMask)
                                                field.onChange(masked)
                                            }}
                                            placeholder={referenceData.config?.phoneMask ? referenceData.config.phoneMask.replace(/9/g, '_') : '0__ ___ __ __'}
                                        />
                                    )}
                                />
                                {errors.client?.natel_client && (
                                    <span className="form-error">{errors.client.natel_client.message}</span>
                                )}
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('client.phone')}</label>
                                <Controller
                                    control={control}
                                    name="client.fix_client"
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="tel"
                                            className="form-input"
                                            onChange={(e) => {
                                                const masked = applyPhoneMask(e.target.value, referenceData.config?.phoneMask)
                                                field.onChange(masked)
                                            }}
                                            placeholder={referenceData.config?.phoneMask ? referenceData.config.phoneMask.replace(/9/g, '_') : '0__ ___ __ __'}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">{t('client.mobile2')}</label>
                                <Controller
                                    control={control}
                                    name="client.natel2_client"
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="tel"
                                            className="form-input"
                                            onChange={(e) => {
                                                const masked = applyPhoneMask(e.target.value, referenceData.config?.phoneMask)
                                                field.onChange(masked)
                                            }}
                                            placeholder={referenceData.config?.phoneMask ? referenceData.config.phoneMask.replace(/9/g, '_') : '0__ ___ __ __'}
                                        />
                                    )}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('client.phone2')}</label>
                                <Controller
                                    control={control}
                                    name="client.fix2_client"
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="tel"
                                            className="form-input"
                                            onChange={(e) => {
                                                const masked = applyPhoneMask(e.target.value, referenceData.config?.phoneMask)
                                                field.onChange(masked)
                                            }}
                                            placeholder={referenceData.config?.phoneMask ? referenceData.config.phoneMask.replace(/9/g, '_') : '0__ ___ __ __'}
                                        />
                                    )}
                                />
                            </div>
                        </div>



                        {/* Address Block */}
                        <div className="address-section-block">
                            <h4 className="section-title">
                                <MapPin size={16} />
                                {t('client.address')}
                            </h4>

                            <div className="form-row">
                                <div className="form-group" style={{ flex: 2 }}>
                                    <label className="form-label">{t('client.address')} *</label>
                                    <Controller
                                        control={control}
                                        name="client.adresse_client.adresse"
                                        rules={{ required: t('validation.required') }}
                                        render={({ field }) => (
                                            <AddressAutocomplete
                                                value={field.value}
                                                onChange={field.onChange}
                                                onSelect={(data) => {
                                                    field.onChange(data.adresse)
                                                    setValue('client.adresse_client.npa', data.npa, { shouldValidate: true })
                                                    setValue('client.adresse_client.ville', data.ville, { shouldValidate: true })
                                                }}
                                                error={!!errors.client?.adresse_client?.adresse}
                                                placeholder={t('client.address')}
                                            />
                                        )}
                                    />
                                    {errors.client?.adresse_client?.adresse && (
                                        <span className="form-error">{errors.client.adresse_client.adresse.message}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('client.floor')}</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        {...register('client.adresse_client.etage')}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">{t('client.npa')} *</label>
                                    <input
                                        type="text"
                                        className={`form-input ${errors.client?.adresse_client?.npa ? 'error' : ''}`}
                                        {...register('client.adresse_client.npa', { required: t('validation.required') })}
                                    />
                                    {errors.client?.adresse_client?.npa && (
                                        <span className="form-error">{errors.client.adresse_client.npa.message}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('client.city')} *</label>
                                    <input
                                        type="text"
                                        className={`form-input ${errors.client?.adresse_client?.ville ? 'error' : ''}`}
                                        {...register('client.adresse_client.ville', { required: t('validation.required') })}
                                    />
                                    {errors.client?.adresse_client?.ville && (
                                        <span className="form-error">{errors.client.adresse_client.ville.message}</span>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">{t('client.indications')}</label>
                                <textarea
                                    className="form-textarea"
                                    rows={3}
                                    {...register('client.adresse_client.indications')}
                                />
                            </div>
                        </div>
                    </div>
                )

            case 1:
                return (
                    <div className="wizard-step">
                        <div className="step-header">
                            <h3>{t('family.title')}</h3>
                            <button type="button" className="btn btn-secondary" onClick={addFamilyMember}>
                                <Plus size={18} />
                                {t('family.add')}
                            </button>
                        </div>

                        {familleFields.length === 0 ? (
                            <div className="empty-state">
                                <Users size={48} className="text-muted" />
                                <p className="text-muted">{t('family.noMembers')}</p>
                                <button type="button" className="btn btn-primary" onClick={addFamilyMember}>
                                    <Plus size={18} />
                                    {t('family.add')}
                                </button>
                            </div>
                        ) : (
                            <div className="family-list">
                                {familleFields.map((field, index) => (
                                    <div key={field.id} className="family-card">
                                        <div className="family-card-header">
                                            <span>{t('family.title')} #{index + 1}</span>
                                            <button
                                                type="button"
                                                className="btn btn-icon btn-ghost"
                                                onClick={() => remove(index)}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                        <div className="family-card-body">
                                            {/* Line 1: Type Famille */}
                                            <div className="form-row">
                                                <div className="form-group" style={{ width: 'auto', minWidth: '150px', flex: '0 0 auto' }}>
                                                    <label className="form-label">{t('family.type')}</label>
                                                    <select
                                                        className="form-select"
                                                        {...register(`famille.${index}.typeFamille`)}
                                                    >
                                                        <option value="Adulte">{t('family.adult')}</option>
                                                        <option value="Enfant">{t('family.child')}</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Line 2: Prénom + Age/DOB */}
                                            <div className="form-row">
                                                <div className="form-group" style={{ flex: 1 }}>
                                                    <label className="form-label">{t('family.firstName')}</label>
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        {...register(`famille.${index}.prenomFamille`)}
                                                    />
                                                </div>

                                                <div className="form-group" style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                                                        <div style={{ minWidth: '100px' }}>
                                                            <label className="form-label" style={{ display: 'block' }}>Type</label>
                                                            <select
                                                                className="form-select"
                                                                {...register(`famille.${index}.ageType`)}
                                                            >
                                                                <option value="dob">Date</option>
                                                                <option value="age">Age</option>
                                                            </select>
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            {(familyValues?.[index]?.ageType || 'dob') === 'dob' ? (
                                                                <Controller
                                                                    control={control}
                                                                    name={`famille.${index}.datenaissanceFamille`}
                                                                    render={({ field }) => (
                                                                        <DatePicker
                                                                            selected={field.value ? new Date(field.value) : null}
                                                                            onChange={(date) => field.onChange(date ? date.toISOString() : '')}
                                                                            dateFormat="dd.MM.yyyy"
                                                                            showYearDropdown
                                                                            scrollableYearDropdown
                                                                            yearDropdownItemNumber={100}
                                                                            className="form-input"
                                                                            placeholderText="dd.mm.yyyy"
                                                                            locale={i18n.language.startsWith('fr') ? 'fr' : undefined}
                                                                            autoComplete='off'
                                                                            maxDate={new Date()}
                                                                        />
                                                                    )}
                                                                />
                                                            ) : (
                                                                <input
                                                                    type="number"
                                                                    className="form-input"
                                                                    placeholder="Age"
                                                                    {...register(`famille.${index}.age`)}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label className="form-label">{t('family.healthState')}</label>
                                                    <select
                                                        className="form-select"
                                                        {...register(`famille.${index}.etatSanteFamille`)}
                                                    >
                                                        {referenceData.etats_sante.map((etat) => (
                                                            <option key={etat.code} value={etat.code}>
                                                                {etat.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="form-group">
                                                    <label className="form-label">{t('family.franchise')}</label>
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        {...register(`famille.${index}.franchiseFamille`)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label className="form-label">{t('family.lamal')}</label>
                                                    <select
                                                        className="form-select"
                                                        {...register(`famille.${index}.lamalFamille`)}
                                                    >
                                                        <option value="">-</option>
                                                        {referenceData.lamal.map((option) => (
                                                            <option key={option.code} value={option.code}>
                                                                {option.nom}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="form-group">
                                                    <label className="form-label">{t('family.lca')}</label>
                                                    <select
                                                        className="form-select"
                                                        {...register(`famille.${index}.lcaFamille`)}
                                                    >
                                                        <option value="">-</option>
                                                        {referenceData.lca.map((option) => (
                                                            <option key={option.code} value={option.code}>
                                                                {option.nom}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="form-group">
                                                    <label className="form-label">{t('family.since')}</label>
                                                    <input
                                                        type="number"
                                                        className="form-input"
                                                        {...register(`famille.${index}.depuisFamille`)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )

            case 2:
                return (
                    <div className="wizard-step">
                        <h3>{t('rendezvous.wizard.step3')}</h3>

                        <div className="summary-section">
                            <h4>{t('rendezvous.title')}</h4>
                            <div className="summary-grid">
                                <div>
                                    <span className="text-muted">{t('rendezvous.date')}</span>
                                    <strong>{formData.rdvDate ? new Date(formData.rdvDate).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</strong>
                                </div>
                            </div>
                        </div>

                        <div className="summary-section">
                            <h4>Client</h4>
                            <div className="summary-grid">
                                <div>
                                    <span className="text-muted">{t('client.lastName')} {t('client.firstName')}</span>
                                    <strong>{formData.client?.prenom_client} {formData.client?.nom_client}</strong>
                                </div>
                                <div>
                                    <span className="text-muted">{t('client.mobile')}</span>
                                    <strong>{formData.client?.natel_client}</strong>
                                </div>
                                <div>
                                    <span className="text-muted">{t('client.address')}</span>
                                    <strong>
                                        {formData.client?.adresse_client?.adresse}, {formData.client?.adresse_client?.npa} {formData.client?.adresse_client?.ville}
                                    </strong>
                                </div>
                            </div>
                        </div>

                        {formData.famille?.length > 0 && (
                            <div className="summary-section">
                                <h4>{t('family.title')} ({formData.famille.length})</h4>
                                <div className="summary-list">
                                    {formData.famille.map((member, index) => (
                                        <div key={index} className="summary-item">
                                            <span className={`badge ${member.typeFamille === 'Adulte' ? 'badge-primary' : 'badge-info'}`}>
                                                {member.typeFamille}
                                            </span>
                                            <strong>{member.prenomFamille}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="form-group mt-4">
                            <label className="form-label">{t('rendezvous.comment')}</label>
                            <textarea
                                className="form-textarea"
                                rows={4}
                                {...register('commentaireAgent_rdv')}
                                placeholder={t('rendezvous.commentPlaceholder')}
                            />
                        </div>
                    </div>
                )

            default:
                return null
        }
    }

    return (
        <div className="rdv-add-page">
            <div className="page-header">
                <button className="btn btn-ghost" onClick={() => navigate('/rendezvous')}>
                    <ArrowLeft size={18} />
                    {t('common.back')}
                </button>
                <h1 className="page-title">{t('rendezvous.add')}</h1>
            </div>

            <div className="card wizard-card">
                {/* Steps */}
                <div className="wizard-steps">
                    {steps.map((step, index) => (
                        <div
                            key={step.key}
                            className={`wizard-step-item ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                        >
                            <div className="wizard-step-icon">
                                {index < currentStep ? <Check size={18} /> : <step.icon size={18} />}
                            </div>
                            <span className="wizard-step-label">{t(`rendezvous.wizard.step${index + 1}`)}</span>
                        </div>
                    ))}
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="wizard-content">
                        {renderStep()}
                    </div>

                    <div className="wizard-footer">
                        {currentStep > 0 && (
                            <button type="button" className="btn btn-secondary" onClick={previousStep}>
                                <ArrowLeft size={18} />
                                {t('common.previous')}
                            </button>
                        )}
                        <div style={{ flex: 1 }} />
                        {currentStep < steps.length - 1 ? (
                            <button type="button" className="btn btn-primary" onClick={nextStep}>
                                {t('common.next')}
                                <ArrowRight size={18} />
                            </button>
                        ) : (
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Loader2 size={18} className="spin" />
                                        {t('common.loading')}
                                    </>
                                ) : (
                                    <>
                                        <Check size={18} />
                                        {t('common.save')}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    )
}
